import Header from '../Header';
import Footer from '../Footer';
import {termsVersionLabel} from '../../../shared/terms';

export default function Terms() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Terms of Service
            </h1>

            <p className="text-sm text-gray-500 mb-8">
              Last updated: {termsVersionLabel()}
            </p>

            <div className="prose prose-gray max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Acceptance of terms
                </h2>
                <p className="text-gray-700 mb-4">
                  By accessing and using Pabs (www.pabs.co.nz), you accept and agree to
                  be bound by the terms and provisions of this agreement.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Description of service
                </h2>
                <p className="text-gray-700 mb-4">
                  Pabs is a strategic planning platform that uses AI to help users transform
                  vague ideas into decision-ready plans with explicit assumptions, confidence
                  scores, and strategic context.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  User responsibilities
                </h2>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Provide accurate information when creating your account</li>
                  <li>Keep your password secure and confidential</li>
                  <li>Use the service in compliance with all applicable laws</li>
                  <li>Not attempt to compromise the security of the platform</li>
                  <li>Not use the service to generate harmful or illegal content</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  AI-generated content and limitations
                </h2>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-4">
                  <p className="text-gray-900 mb-3">
                    <strong>Important disclaimer:</strong> Pabs uses AI to generate strategic
                    analysis, recommendations, and decisions. While we strive for accuracy:
                  </p>
                  <ul className="space-y-2 text-gray-800 text-sm">
                    <li>• AI outputs may contain errors or inaccuracies</li>
                    <li>• Confidence scores indicate likelihood, not certainty</li>
                    <li>• You are responsible for validating assumptions and claims</li>
                    <li>• Strategic decisions remain your responsibility</li>
                  </ul>
                </div>
                <p className="text-gray-700">
                  Pabs provides tools for better decision-making but does not guarantee
                  specific outcomes. Always verify critical information independently.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Intellectual property
                </h2>
                <p className="text-gray-700 mb-4">
                  <strong>Your content:</strong> You retain all rights to the strategic plans,
                  briefs, and content you create in Pabs.
                </p>
                <p className="text-gray-700 mb-4">
                  <strong>Our platform:</strong> The Pabs platform, including its design,
                  features, and underlying technology, is protected by copyright and other
                  intellectual property laws.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  MCP integrations
                </h2>
                <p className="text-gray-700 mb-4">
                  When you connect Pabs to an AI tool, by installing Pabs in your project or by adding a Pabs connection link to the tool:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>You are responsible for managing access tokens and connection links</li>
                  <li>Third-party tools operate under their own terms of service</li>
                  <li>You grant permission for connected tools to read your strategy</li>
                  <li>You permit the tool you connect to send Pabs a summary of your project, such as its purpose, audience, what has been built, your goals and references to the documents it used, so Pabs can create and update your Brief, strategy and related work</li>
                  <li>Connected tools are instructed to send summaries only, never passwords, keys or other credentials, personal details of anyone, or raw records such as transcripts, logs or data exports. Pabs rejects content that looks like credentials or email addresses. You remain responsible for not directing a tool to share confidential or personal information</li>
                  <li>Tokens and connections can be revoked at any time from your account</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Subscription and payments
                </h2>
                <p className="text-gray-700 mb-4">
                  <strong>Free tier:</strong> Limited access to core features as described
                  on our website.
                </p>
                <p className="text-gray-700 mb-4">
                  <strong>Paid plans:</strong> Billing occurs monthly. You may cancel at
                  any time, with access continuing until the end of your billing period.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Termination
                </h2>
                <p className="text-gray-700 mb-4">
                  We reserve the right to suspend or terminate accounts that violate these
                  terms or engage in abusive behavior. You may delete your account at any
                  time from your account settings.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Limitation of liability
                </h2>
                <p className="text-gray-700 mb-4">
                  Pabs is provided "as is" without warranties of any kind. We are not liable
                  for decisions made based on AI-generated content, business outcomes, or
                  indirect damages arising from use of the service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Changes to terms
                </h2>
                <p className="text-gray-700 mb-4">
                  We may update these terms from time to time. We will notify users of
                  significant changes via email or through the platform.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Contact
                </h2>
                <p className="text-gray-700">
                  Questions about these terms? Contact us at{' '}
                  <a href="mailto:legal@pabs.co.nz" className="text-blue-600 hover:text-blue-700">
                    legal@pabs.co.nz
                  </a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
